import { CSSResultGroup, html, LitElement , nothing } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './carousel.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { carouselItemClickEvent } from '@cloudsuite/mosaic-elements'

export const controlsPositions = ['none', 'bottom', 'sides', 'top'] as const
export type controlsPositionTypes = typeof controlsPositions

const DRAG_THRESHOLD_PX = 5

@customElement('csc-carousel')
export class CSCCarousel extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Boolean })
  pagination = false

  @property({ type: String, attribute: 'controls-position' })
  controlsPosition: controlsPositionTypes[number] = 'bottom'

  @property({ type: Boolean, attribute: 'auto-scroll' })
  autoScroll = false

  @property({ type: Number, attribute: 'scroll-interval' })
  scrollInterval = 3000

  @property({ type: Number, attribute: 'items-per-view-desktop' })
  itemsPerViewDesktop = 3

  @property({ type: Number, attribute: 'items-per-view-tablet' })
  itemsPerViewTablet = 2

  @property({ type: Number, attribute: 'items-per-view-mobile' })
  itemsPerViewMobile = 1

  @property({ type: String })
  override title = ''

  @property({ type: String })
  description = ''

  @property({ type: Boolean, attribute: 'full-width' })
  fullWidth = false

@property({ type: Boolean, attribute: 'stretch-tiles' })
  stretchTiles = false

  @state()
  activeSlide: number = 0

  @state()
  x: number = 0

  @state()
  private hasMinimumItemsToDisplayControls = false

  @state()
  private itemsPerView = this.itemsPerViewDesktop

  @queryAssignedElements({ flatten: true })
  _slides!: Array<HTMLElement>

  @query('.slides-wrapper')
  private _slidesWrapper!: HTMLElement

  private autoScrollIntervalId: number | undefined

  // Drag state
  private dragStartClientX: number | null = null
  private dragOriginClientX: number | null = null
  @state()
  private isDragging = false

  // Wheel snap debounce
  private wheelSnapTimeout: number | undefined

  override connectedCallback() {
    super.connectedCallback()
    this.updateItemsPerView()
    window.addEventListener('resize', this.updateItemsPerView)

    if (this.autoScroll) {
      this.startAutoScroll()
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('resize', this.updateItemsPerView)
    this.stopAutoScroll()

    // Clean up listeners if attached
    if (this._slidesWrapper) {
      this._slidesWrapper.removeEventListener('pointerdown', this.onPointerDown, { capture: true } as any)
      this._slidesWrapper.removeEventListener('pointermove', this.onPointerMove, { capture: true } as any)
      this._slidesWrapper.removeEventListener('pointerup', this.onPointerUp, { capture: true } as any)
      this._slidesWrapper.removeEventListener('pointercancel', this.onPointerUp, { capture: true } as any)
      this._slidesWrapper.removeEventListener('wheel', this.onWheel as EventListener, { passive: false } as any)
      this._slidesWrapper.removeEventListener('dragstart', this.onDragStart as EventListener)
    }
  }

  override updated(changedProps: Map<string, unknown>) {
    super.updated(changedProps)

    this.updateControlVisibility()

    if (changedProps.has('autoScroll') || changedProps.has('scrollInterval')) {
      if (this.autoScroll) {
        this.startAutoScroll()
      } else {
        this.stopAutoScroll()
      }
    }
  }

  private updateItemsPerView = () => {
    const width = window.innerWidth

    if (width >= 992) {
      this.itemsPerView = this.itemsPerViewDesktop
    } else if (width >= 768) {
      this.itemsPerView = this.itemsPerViewTablet
    } else {
      this.itemsPerView = this.itemsPerViewMobile
    }

    this.scrollToSlide(false)
    this.updateControlVisibility()
  }

  private get wholeItemsPerView() {
    return Math.floor(this.itemsPerView)
  }

  private updateControlVisibility() {
    // Ensure _slides is populated before checking length
    if (this._slides && this._slides.length > 0) {
      this.hasMinimumItemsToDisplayControls = this._slides.length > this.wholeItemsPerView
    } else {
      this.hasMinimumItemsToDisplayControls = false
    }
  }

  protected override firstUpdated(): void {
    // Attach capture-phase listeners to ensure slotted content cannot block dragging
    if (this._slidesWrapper) {
      this._slidesWrapper.addEventListener('pointerdown', this.onPointerDown, { capture: true })
      this._slidesWrapper.addEventListener('pointermove', this.onPointerMove, { capture: true })
      this._slidesWrapper.addEventListener('pointerup', this.onPointerUp, { capture: true })
      this._slidesWrapper.addEventListener('pointercancel', this.onPointerUp, { capture: true })
      // Wheel for trackpad/mouse horizontal scroll
      this._slidesWrapper.addEventListener('wheel', this.onWheel as EventListener, { passive: false, capture: true })
      // Prevent native drags
      this._slidesWrapper.addEventListener('dragstart', this.onDragStart as EventListener)
    }
    // Fallback: listen on host too
    this.addEventListener('wheel', this.onWheel as EventListener, { passive: false } as any)
  }

  private startAutoScroll() {
    this.stopAutoScroll()

    this.autoScrollIntervalId = window.setInterval(() => {
      if (!this._slides.length) { return }

      const maxSlide = Math.max(0, this._slides.length - this.wholeItemsPerView)

      const hasReachedEnd = this.activeSlide >= maxSlide
      const nextIndex = hasReachedEnd ? 0 : this.activeSlide + 1

      this.goToSlide(nextIndex)
    }, this.scrollInterval)
  }

  private stopAutoScroll() {
    if (this.autoScrollIntervalId) {
      clearInterval(this.autoScrollIntervalId)
      this.autoScrollIntervalId = undefined
    }
  }

  private prevClick() {
    if (this.activeSlide - 1 < 0) {
      return
    }
    this.goToSlide(this.activeSlide - 1)
  }

  private nextClick() {
    const maxSlide = Math.max(0, this._slides.length - this.wholeItemsPerView)
    if (this.activeSlide + 1 > maxSlide) {
      return
    }
    this.goToSlide(this.activeSlide + 1)
  }

  // Handle all go to slides including pagination
  private goToSlide(index: number, smooth = true) {
    if (!this._slides.length) {
      console.debug('[carousel] goToSlide BLOCKED: no slides')
      return
    }

    const maxSlide = Math.max(0, this._slides.length - this.wholeItemsPerView)
    const nextIndex = Math.min(index, maxSlide)
    if (nextIndex === this.activeSlide) {
      return
    }
    this.activeSlide = nextIndex
    this.scrollToSlide(smooth)
  }

  // Go to actual slide
  private scrollToSlide(smooth = true) {
    const wrapper = this._slidesWrapper
    if (wrapper && this._slides?.[this.activeSlide]) {
      const target = this._slides[this.activeSlide]
      const targetRect = (target as HTMLElement).getBoundingClientRect()
      const wrapperRect = wrapper.getBoundingClientRect()
      const scrollLeft = wrapper.scrollLeft + (targetRect.left - wrapperRect.left)
      wrapper.scrollTo({ left: scrollLeft, behavior: smooth ? 'smooth' : 'instant' })
    } else {
      const slideWidth = 100 / this.itemsPerView
      const maxOffset = slideWidth * (this._slides.length - this.wholeItemsPerView)
      const rawOffset = slideWidth * this.activeSlide
      this.x = Math.min(rawOffset, maxOffset)
    }
    this.updateControlVisibility()
  }

  // Drag / swipe handlers
  private onPointerDown = (event: PointerEvent) => {
    if (!this._slidesWrapper || event.button !== 0) { return }
    this.dragStartClientX = event.clientX
    this.dragOriginClientX = event.clientX
  }

  private onPointerMove = (event: PointerEvent) => {
    if (this.dragStartClientX === null || this.dragOriginClientX === null || !this._slidesWrapper) { return }
    if (!this.isDragging) {
      if (Math.abs(event.clientX - this.dragOriginClientX) < DRAG_THRESHOLD_PX) { return }
      this.isDragging = true
      this.capturePointer(event.pointerId)
      this.stopAutoScroll()
      this._slidesWrapper.classList.add('dragging')
    }
    const deltaPx = this.dragStartClientX - event.clientX
    this._slidesWrapper.scrollLeft += deltaPx
    this.dragStartClientX = event.clientX
  }

  private onPointerUp = (event: PointerEvent) => {
    this.dragStartClientX = null
    this.dragOriginClientX = null
    if (!this.isDragging) { return }
    if (this._slidesWrapper?.hasPointerCapture?.(event.pointerId)) { this._slidesWrapper.releasePointerCapture(event.pointerId) }
    this.isDragging = false
    this._slidesWrapper?.classList.remove('dragging')
    this.suppressNextClick()
    if (this._slidesWrapper && this._slides.length) {
      const wrapper = this._slidesWrapper
      const wrapperRect = wrapper.getBoundingClientRect()
      let nearestIndex = 0
      let minDist = Number.POSITIVE_INFINITY
      this._slides.forEach((el, idx) => {
        const dist = Math.abs(el.getBoundingClientRect().left - wrapperRect.left)
        if (dist < minDist) { minDist = dist; nearestIndex = idx }
      })
      this.goToSlide(nearestIndex, false)
    }
    if (this.autoScroll) {
      this.startAutoScroll()
    }
  }

  private capturePointer(pointerId: number) {
    try {
      this._slidesWrapper.setPointerCapture(pointerId)
    } catch {
      return
    }
  }

  private suppressNextClick() {
    const wrapper = this._slidesWrapper
    if (!wrapper) { return }
    const swallow = (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
    }
    wrapper.addEventListener('click', swallow, { capture: true, once: true })
    window.setTimeout(() => wrapper.removeEventListener('click', swallow, { capture: true }), 0)
  }

  private onDragStart = (event: DragEvent) => {
    // Prevent native image drag which interferes with pointer events
    event.preventDefault()
  }

  private onWheel = (event: WheelEvent) => {
    if (!this._slidesWrapper || !this._slides.length) { return }
    const absX = Math.abs(event.deltaX)
    const absY = Math.abs(event.deltaY)
    if (absX < absY && !event.shiftKey) { return }
    // Let native scrolling happen by adjusting scrollLeft
    this.stopAutoScroll()
    // Temporarily disable snapping to avoid jitter mid-gesture
    this._slidesWrapper.classList.add('dragging')
    this._slidesWrapper.scrollLeft += (absX > 0 ? event.deltaX : event.deltaY)

    // Debounce snapping to nearest slide
    if (this.wheelSnapTimeout) {
      clearTimeout(this.wheelSnapTimeout)
      this.wheelSnapTimeout = undefined
    }
    this.wheelSnapTimeout = window.setTimeout(() => {
      if (!this._slidesWrapper) { return }
      this._slidesWrapper.classList.remove('dragging')
      const wrapperRect = this._slidesWrapper.getBoundingClientRect()
      let nearestIndex = 0
      let minDist = Number.POSITIVE_INFINITY
      this._slides.forEach((el, idx) => {
        const dist = Math.abs(el.getBoundingClientRect().left - wrapperRect.left)
        if (dist < minDist) { minDist = dist; nearestIndex = idx }
      })
      this.goToSlide(nearestIndex)
      if (this.autoScroll) { this.startAutoScroll() }
    }, 100)
  }

  private renderCarouselPagination = () => html`
    <cse-carousel-pagination
      .pagination="${this.pagination}"
      .navigation="${this.controlsPosition === 'bottom'}"
      .disabled="${!this._slides.length}"
      .selectedItemIndex="${this.activeSlide}"
      .itemsLength="${Math.ceil(this._slides.length - this.wholeItemsPerView + 1)}"
      .previousText="${t('carousel_previous_slide', {
          ns: 'csc-carousel',
          default: 'Previous'
      })}"
      .nextText="${t('carousel_next_slide', {
          ns: 'csc-carousel',
          default: 'Next'
      })}"
      @prev-click="${this.prevClick}"
      @next-click="${this.nextClick}"
      @carousel-item-click="${(e: carouselItemClickEvent) => this.goToSlide(e.index)}"
    ></cse-carousel-pagination>
  `

  private renderHeader = () => {
    return (this.title || this.description) ? html`
      <div class="header">
        <cse-text-group>
          ${this.title ? html`<cse-text slot="heading" variant="heading-2">${this.title}</cse-text>` : nothing}
          ${this.description ? html`<cse-text variant="body">${this.description}</cse-text>` : nothing}
        </cse-text-group>
      </div>
    ` : nothing
  }

  private renderHeaderRow = () => {
    const showTopControls = this.controlsPosition === 'top' && this.hasMinimumItemsToDisplayControls
    if (!this.title && !this.description && !showTopControls) return nothing

    return html`
      <cse-group class="header-row" justify="space-between" alignment="center">
        ${this.renderHeader()}
        ${showTopControls ? this.renderControls('top') : nothing}
      </cse-group>
    `
  }

  private renderControls = (position: string) => html`
    <div class="${position}-controls">
      <cse-icon-button
        class="control-previous"
        shadow
        @click="${this.prevClick}"
        aria-label="View previous image"
        ?disabled=${this.activeSlide === 0}
      >
        <cse-icon icon-role="cse-carousel-pagination.previous"></cse-icon>
      </cse-icon-button>
      <cse-icon-button
        class="control-next"
        shadow
        @click="${this.nextClick}"
        aria-label="View next image"
        ?disabled=${this.activeSlide === this._slides.length - this.wholeItemsPerView}
      >
        <cse-icon icon-role="cse-carousel-pagination.next"></cse-icon>
      </cse-icon-button>
    </div>
  `

  override render() {
    return html`
      <div class="csc-carousel" ?full-width=${this.fullWidth} ?stretch-tiles=${this.stretchTiles} ?partial-view=${this.itemsPerView % 1 !== 0}>
        ${this.renderHeaderRow()}

        <div class="body">
          <div class="carousel-wrapper">
            <div class="slides-wrapper"
              @dragstart=${this.onDragStart}
              @wheel=${this.onWheel}
            >
              <div class="carousel" style="--items-per-view: ${this.itemsPerView};">
                <slot @slotchange="${() => {
                  this.requestUpdate()
                  this.updateControlVisibility()
                }}"></slot>
              </div>
            </div>
            ${this.controlsPosition === 'sides' && this.hasMinimumItemsToDisplayControls
              ? this.renderControls('sides')
              : nothing}
          </div>

          ${this.pagination && this.hasMinimumItemsToDisplayControls
            ? this.renderCarouselPagination()
          : nothing}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-carousel': CSCCarousel
  }
}
